import { supabase } from './supabaseService';
import { Client, ClientStatus, ClientTag } from '../types/multiTenant';

/**
 * Client Service
 * Handles all client-related operations with Supabase
 * Clients are automatically filtered by organization_id via RLS
 */
class ClientService {
  /**
   * List all clients for an organization
   */
  async listClients(organizationId: string): Promise<Client[]> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .order('full_name');

      if (error) {
        console.error('Error listing clients:', error);
        return [];
      }

      return (data || []).map(this.transformClient);
    } catch (error) {
      console.error('Error in listClients:', error);
      return [];
    }
  }

  /**
   * Get client by ID
   */
  async getClientById(clientId: string): Promise<Client | null> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .eq('deleted', false)
        .single();

      if (error) {
        console.error('Error fetching client:', error);
        return null;
      }

      return this.transformClient(data);
    } catch (error) {
      console.error('Error in getClientById:', error);
      return null;
    }
  }

  /**
   * Create new client
   */
  async createClient(data: {
    organizationId: string;
    fullName: string;
    dateOfBirth?: string;
    gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    email?: string;
    phone?: string;
    mobile?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
    customData?: Record<string, any>;
    sessionPrice?: number;
    currency?: string;
    notes?: string;
    status?: ClientStatus;
    tags?: ClientTag[];
    createdBy: string;
  }): Promise<Client | null> {
    try {
      const { data: clientData, error } = await supabase
        .from('clients')
        .insert({
          organization_id: data.organizationId,
          full_name: data.fullName,
          date_of_birth: data.dateOfBirth,
          gender: data.gender,
          email: data.email,
          phone: data.phone,
          mobile: data.mobile,
          address: data.address,
          city: data.city,
          state: data.state,
          zip_code: data.zipCode,
          country: data.country || 'Brazil',
          custom_data: data.customData || {},
          session_price: data.sessionPrice,
          currency: data.currency || 'BRL',
          notes: data.notes,
          status: data.status || 'active',
          tags: data.tags || [],
          created_by: data.createdBy,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating client:', error);
        return null;
      }

      return this.transformClient(clientData);
    } catch (error) {
      console.error('Error in createClient:', error);
      return null;
    }
  }

  /**
   * Update client
   */
  async updateClient(
    clientId: string,
    updates: Partial<Client>
  ): Promise<Client | null> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .update({
          full_name: updates.fullName,
          date_of_birth: updates.dateOfBirth,
          gender: updates.gender,
          email: updates.email,
          phone: updates.phone,
          mobile: updates.mobile,
          address: updates.address,
          city: updates.city,
          state: updates.state,
          zip_code: updates.zipCode,
          country: updates.country,
          custom_data: updates.customData,
          session_price: updates.sessionPrice,
          currency: updates.currency,
          notes: updates.notes,
          status: updates.status,
          tags: updates.tags,
        })
        .eq('id', clientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating client:', error);
        return null;
      }

      return this.transformClient(data);
    } catch (error) {
      console.error('Error in updateClient:', error);
      return null;
    }
  }

  /**
   * Delete client (soft delete)
   */
  async deleteClient(clientId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .update({
          deleted: true,
        })
        .eq('id', clientId)
        .select();

      if (error) {
        console.error('Error deleting client:', error);
        return false;
      }

      // Return true only if at least one row was affected
      return data && data.length > 0;
    } catch (error) {
      console.error('Error in deleteClient:', error);
      return false;
    }
  }

  /**
   * Search clients with full-text search
   */
  async searchClients(
    organizationId: string,
    query: string
  ): Promise<Client[]> {
    try {
      // Use PostgreSQL full-text search
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .textSearch('fts', query, {
          type: 'websearch',
          config: 'portuguese',
        })
        .order('full_name');

      if (error) {
        // Fallback to ILIKE search if full-text search fails
        return this.searchClientsILike(organizationId, query);
      }

      return (data || []).map(this.transformClient);
    } catch (error) {
      console.error('Error in searchClients:', error);
      return this.searchClientsILike(organizationId, query);
    }
  }

  /**
   * Fallback search using ILIKE
   */
  private async searchClientsILike(
    organizationId: string,
    query: string
  ): Promise<Client[]> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .or(`full_name.ilike.%${query}%,email.ilike.%${query}%,phone.ilike.%${query}%,notes.ilike.%${query}%`)
        .order('full_name');

      if (error) {
        console.error('Error in ILIKE search:', error);
        return [];
      }

      return (data || []).map(this.transformClient);
    } catch (error) {
      console.error('Error in searchClientsILike:', error);
      return [];
    }
  }

  /**
   * Get clients by tag
   */
  async getClientsByTag(
    organizationId: string,
    tagName: string
  ): Promise<Client[]> {
    try {
      // Search for tag by name in the JSONB array
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .filter('tags', 'cs', JSON.stringify([{ name: tagName }]))
        .order('full_name');

      if (error) {
        console.error('Error fetching clients by tag:', error);
        return [];
      }

      return (data || []).map(this.transformClient);
    } catch (error) {
      console.error('Error in getClientsByTag:', error);
      return [];
    }
  }

  /**
   * Get clients by status
   */
  async getClientsByStatus(
    organizationId: string,
    status: ClientStatus
  ): Promise<Client[]> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('deleted', false)
        .eq('status', status)
        .order('full_name');

      if (error) {
        console.error('Error fetching clients by status:', error);
        return [];
      }

      return (data || []).map(this.transformClient);
    } catch (error) {
      console.error('Error in getClientsByStatus:', error);
      return [];
    }
  }

  /**
   * Update client status
   */
  async updateClientStatus(
    clientId: string,
    status: ClientStatus
  ): Promise<Client | null> {
    try {
      const { data, error } = await supabase
        .from('clients')
        .update({ status })
        .eq('id', clientId)
        .select()
        .single();

      if (error) {
        console.error('Error updating client status:', error);
        return null;
      }

      return this.transformClient(data);
    } catch (error) {
      console.error('Error in updateClientStatus:', error);
      return null;
    }
  }

  /**
   * Add tag to client
   */
  async addTagToClient(
    clientId: string,
    tag: ClientTag
  ): Promise<Client | null> {
    try {
      // First get current tags
      const client = await this.getClientById(clientId);
      if (!client) return null;

      const currentTags = client.tags || [];
      // Avoid duplicates
      if (currentTags.some(t => t.id === tag.id)) {
        return client;
      }

      const newTags = [...currentTags, tag];

      const { data, error } = await supabase
        .from('clients')
        .update({ tags: newTags })
        .eq('id', clientId)
        .select()
        .single();

      if (error) {
        console.error('Error adding tag to client:', error);
        return null;
      }

      return this.transformClient(data);
    } catch (error) {
      console.error('Error in addTagToClient:', error);
      return null;
    }
  }

  /**
   * Remove tag from client
   */
  async removeTagFromClient(
    clientId: string,
    tagId: string
  ): Promise<Client | null> {
    try {
      const client = await this.getClientById(clientId);
      if (!client) return null;

      const newTags = (client.tags || []).filter(t => t.id !== tagId);

      const { data, error } = await supabase
        .from('clients')
        .update({ tags: newTags })
        .eq('id', clientId)
        .select()
        .single();

      if (error) {
        console.error('Error removing tag from client:', error);
        return null;
      }

      return this.transformClient(data);
    } catch (error) {
      console.error('Error in removeTagFromClient:', error);
      return null;
    }
  }

  /**
   * Get all unique tags used in organization
   */
  async getOrganizationTags(organizationId: string): Promise<ClientTag[]> {
    try {
      const clients = await this.listClients(organizationId);
      const tagMap = new Map<string, ClientTag>();

      clients.forEach(client => {
        (client.tags || []).forEach(tag => {
          if (!tagMap.has(tag.id)) {
            tagMap.set(tag.id, tag);
          }
        });
      });

      return Array.from(tagMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error in getOrganizationTags:', error);
      return [];
    }
  }

  /**
   * Get client statistics
   */
  async getClientStats(organizationId: string): Promise<{
    total: number;
    active: number;
    byGender: Record<string, number>;
    byTag: Record<string, number>;
    byStatus: Record<ClientStatus, number>;
  }> {
    try {
      const clients = await this.listClients(organizationId);

      const stats = {
        total: clients.length,
        active: clients.filter(c => c.status === 'active' || !c.status).length,
        byGender: {} as Record<string, number>,
        byTag: {} as Record<string, number>,
        byStatus: {
          active: 0,
          paused: 0,
          discharged: 0,
          waiting: 0,
        } as Record<ClientStatus, number>,
      };

      clients.forEach((client) => {
        // Count by gender
        if (client.gender) {
          stats.byGender[client.gender] = (stats.byGender[client.gender] || 0) + 1;
        }

        // Count by tags
        if (client.tags) {
          client.tags.forEach((tag) => {
            stats.byTag[tag.name] = (stats.byTag[tag.name] || 0) + 1;
          });
        }

        // Count by status
        const status = client.status || 'active';
        stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error in getClientStats:', error);
      return {
        total: 0,
        active: 0,
        byGender: {},
        byTag: {},
        byStatus: { active: 0, paused: 0, discharged: 0, waiting: 0 },
      };
    }
  }

  /**
   * Check if organization can create a new client based on plan limits
   */
  async canCreateClient(organizationId: string, maxClients: number): Promise<{
    canCreate: boolean;
    currentCount: number;
    limit: number;
    message?: string;
  }> {
    // -1 means unlimited
    if (maxClients === -1) {
      return { canCreate: true, currentCount: 0, limit: -1 };
    }

    try {
      const stats = await this.getClientStats(organizationId);
      const canCreate = stats.active < maxClients;

      return {
        canCreate,
        currentCount: stats.active,
        limit: maxClients,
        message: canCreate
          ? undefined
          : `Limite de ${maxClients} clientes atingido. Faça upgrade do plano para adicionar mais clientes.`
      };
    } catch (error) {
      console.error('Error in canCreateClient:', error);
      // In case of error, allow creation (fail-open for better UX)
      return { canCreate: true, currentCount: 0, limit: maxClients };
    }
  }

  /**
   * Transform database client to app format
   */
  private transformClient(data: any): Client {
    return {
      id: data.id,
      organizationId: data.organization_id,
      fullName: data.full_name,
      dateOfBirth: data.date_of_birth,
      gender: data.gender,
      email: data.email,
      phone: data.phone,
      mobile: data.mobile,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zip_code,
      country: data.country,
      customData: data.custom_data || {},
      sessionPrice: data.session_price,
      currency: data.currency,
      notes: data.notes,
      status: data.status || 'active',
      tags: data.tags || [],
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      deleted: data.deleted || false,
    };
  }
}

export const clientService = new ClientService();
export default clientService;
